// Shared print-friendly basemaps for the map-rendering pillars (Trail Maps,
// Road Trips). High-contrast, label-legible styles that read well on paper.
//
// VECTOR styles (MapTiler) render labels client-side with collision avoidance,
// so place names don't clip at the edges the way baked raster labels do.
// OpenTopoMap / CyclOSM / OSM are keyless raster fallbacks that always work.
//
// Provider keys live in a .env file (restart the dev server after editing).
// Without a key the matching styles are simply hidden, so the page never breaks.
//   REACT_APP_THUNDERFOREST_KEY  → Thunderforest (raster, print-grade hiking)
//   REACT_APP_MAPTILER_KEY       → MapTiler (VECTOR — client-rendered labels)
const TF_KEY = process.env.REACT_APP_THUNDERFOREST_KEY || '';
const MT_KEY = process.env.REACT_APP_MAPTILER_KEY || '';
const tf = (style) => ['a', 'b', 'c'].map((s) => `https://${s}.tile.thunderforest.com/${style}/{z}/{x}/{y}.png?apikey=${TF_KEY}`);
const mt = (style) => `https://api.maptiler.com/maps/${style}/style.json?key=${MT_KEY}`;
const TF_ATTR = 'Maps © Thunderforest · Data © OpenStreetMap contributors';
const MT_ATTR = '© MapTiler © OpenStreetMap contributors';

export const BASEMAPS = {
    // Free (no key) — always available.
    topographic: { label: 'Topographic', kind: 'raster', tiles: ['https://a.tile.opentopomap.org/{z}/{x}/{y}.png', 'https://b.tile.opentopomap.org/{z}/{x}/{y}.png', 'https://c.tile.opentopomap.org/{z}/{x}/{y}.png'], attribution: '© OpenTopoMap (CC-BY-SA) · © OpenStreetMap' },
    vivid: { label: 'Vivid', kind: 'raster', tiles: ['https://a.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png', 'https://b.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png', 'https://c.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png'], attribution: '© CyclOSM · © OpenStreetMap' },
    street: { label: 'Street', kind: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], attribution: '© OpenStreetMap contributors' },
    // Thunderforest (key) — high-contrast, print-grade raster styles.
    landscape: { label: 'Landscape', kind: 'raster', keyed: 'tf', tiles: tf('landscape'), attribution: TF_ATTR },
    outdoors: { label: 'Outdoors', kind: 'raster', keyed: 'tf', tiles: tf('outdoors'), attribution: TF_ATTR },
    // MapTiler (key) — VECTOR styles; labels render client-side (no edge clip).
    'mt-outdoor': { label: 'Trails', kind: 'vector', keyed: 'mt', style: mt('outdoor-v2'), attribution: MT_ATTR },
    'mt-topo': { label: 'Relief', kind: 'vector', keyed: 'mt', style: mt('topo-v2'), attribution: MT_ATTR },
    'mt-streets': { label: 'Streets', kind: 'vector', keyed: 'mt', style: mt('streets-v2'), attribution: MT_ATTR },
};

// A keyed style only appears once its provider key is configured.
const keyAvailable = { tf: !!TF_KEY, mt: !!MT_KEY };

/** [{ id, label }] for every basemap whose provider key (if any) is present. */
export const TILE_STYLES = Object.entries(BASEMAPS)
    .filter(([, v]) => !v.keyed || keyAvailable[v.keyed])
    .map(([id, v]) => ({ id, label: v.label }));

/** A minimal MapLibre style wrapping a single raster tile source. */
export const rasterStyle = (entry) => ({
    version: 8,
    sources: { base: { type: 'raster', tiles: entry.tiles, tileSize: 256, attribution: entry.attribution } },
    layers: [{ id: 'base', type: 'raster', source: 'base' }],
});

/** Resolve a styleId to its basemap entry, falling back to topographic. */
export const basemapEntry = (styleId) => BASEMAPS[styleId] || BASEMAPS.topographic;
